import React, {useState, useMemo, useRef} from 'react'
import MaterialReactTable from 'material-react-table'
import { Box, IconButton } from '@mui/material'
import { ExportToCsv } from 'export-to-csv' //or use your library of choice here
import CloudDownloadIcon from '@mui/icons-material/CloudDownload'
import { CommentsDisabledOutlined, DisplaySettingsRounded } from '@mui/icons-material'

const csvOptions = {
  fieldSeparator: ',',
  quoteStrings: '"',
  decimalSeparator: '.',
  showLabels: true,
  useBom: true,
  useKeysAsHeaders: true
}

const csvExporter = new ExportToCsv(csvOptions)

export default function ReactTable (props) {
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  const handleExportRows = rows => {
    const filteredRows = rows.map((row) => {
      const newRow = { ...row.original };
      Object.keys(newRow).forEach((key) => {
        const colDef = props.cols.find((col) => col.accessorKey === key);
        if (colDef?.meta?.excludeFromExport) {
          delete newRow[key]; // Remove unwanted columns
        }
      });
      return newRow;
    });
    csvExporter.generateCsv(filteredRows);
  }

  // comparison function for data
  const dataRef = useRef();
  const isDataEqual = (newData, oldData) => {
    if (!oldData || !newData) return false;
    if (newData.length !== oldData.length) return false;
    
    return newData.every((newRow, index) => {
      const oldRow = oldData[index];
      if (!oldRow) return false;
      
      const newKeys = Object.keys(newRow);
      const oldKeys = Object.keys(oldRow);
      
      if (newKeys.length !== oldKeys.length) return false;
      
      return newKeys.every(key => {
        // For React elements (like links), compare by their props or key
        if (React.isValidElement(newRow[key]) && React.isValidElement(oldRow[key])) {
          return newRow[key].key === oldRow[key].key;
        }
        return newRow[key] === oldRow[key];
      });
    });
  };

  // Memoize data processing with deep comparison to prevent unnecessary recreations
  const data = useMemo(() => {
    const processedData = props.data
      ? props.data.map(row => {
          const processedRow = { ...row };
          Object.keys(processedRow).forEach(function (key, index) {
            if (processedRow[key] === null) {
              processedRow[key] = ''
            }
          })
          return processedRow;
        })
      : [];

    // Only update if data actually changed
    if (isDataEqual(processedData, dataRef.current)) {
      return dataRef.current;
    }
    
    dataRef.current = processedData;
    return processedData;
  }, [props.data])

    if(!props.cols) {
      return (
        <div> No Data</div>
      )
    }

  // Build state object - include pagination only if enablePagination is true
  const tableState = {
    isLoading: props.isLoading ? true : false,
  }
  
  // Default enablePagination to true if not specified
  const enablePagination = props.enablePagination !== false;
  
  if (enablePagination) {
    tableState.pagination = pagination
  }

  return (
    <MaterialReactTable
      columns={props.cols.map(r => {
        return {...r, size:1}
      })}
      data={data}
      key={props.key ? props.key : ''}
      initialState={{ 
        ...props.initialState,
        showColumnFilters: true, 
        density: 'compact',
        ...(enablePagination && { pagination })
      }}
      muiTableContainerProps={{ sx: { maxHeight: '500px' } }}
      enableStickyHeader
      enableFullScreenToggle={false}
      showSkeletons={true}
      enableFilterRankedResults={false}
      enableGlobalFilter={false} //turn off a feature
      //  manualPagination={true}
      state={tableState}
      onPaginationChange={enablePagination ? setPagination : undefined}
      renderTopToolbarCustomActions={({ table }) => (
        <Box
          sx={{ display: 'flex', gap: '1rem', p: '0.5rem', flexWrap: 'wrap' }}
        >
          <IconButton
            onClick={() => {
              const tableDef = table._getColumnDefs()

              handleExportRows(table.getPrePaginationRowModel().rows)
            }}
            icon={<CloudDownloadIcon />}
          >
            <CloudDownloadIcon />
          </IconButton>
        </Box>
      )}
    />
  )
}
